/**
 * `/__ui/auth` — the fixture harness for the platform sign-in surfaces (M0-F3b §8.1 / §7),
 * the same trick the board, admin, forms and notifications harnesses use: it renders with
 * **no session, no api and no database**, so a served static build can be hit-tested by
 * `tools/browser/verify-sso-local.mjs` on three viewports.
 *
 * `?screen=` selects a surface:
 *   `login` (default, password + the platform button) · `login-sso-only` (passwords off) ·
 *   `login-no-sso` (F3's original page) · `sso-access-denied` · `sso-deactivated` ·
 *   `sso-not-invited` · `sso-disabled` · `sso-expired` · `sso-generic`
 *
 * The login screen reads `system.authInfo`, so the harness answers that one tRPC call from a
 * literal — the *page* is the real component, only its one input is a fixture.
 */
import { useSearchParams } from 'react-router';
import { Login } from './Login';
import { GENERIC_SSO_ERROR, SSO_ERRORS, SsoErrorView } from './SsoError';

type AuthInfo = {
  accountName: string;
  selfJoinDomains: string[];
  authenticated: boolean;
  sso: { enabled: boolean; allowPasswordLogin: boolean; label: string };
};

const INFOS: Record<string, AuthInfo> = {
  login: {
    accountName: 'Eli Z Group',
    selfJoinDomains: [],
    authenticated: false,
    sso: { enabled: true, allowPasswordLogin: true, label: 'Sign in with Eli Z Platform' },
  },
  'login-sso-only': {
    accountName: 'Eli Z Group',
    selfJoinDomains: [],
    authenticated: false,
    sso: { enabled: true, allowPasswordLogin: false, label: 'Sign in with Eli Z Platform' },
  },
  'login-no-sso': {
    accountName: 'Eli Z Group',
    selfJoinDomains: [],
    authenticated: false,
    sso: { enabled: false, allowPasswordLogin: true, label: 'Sign in with Eli Z Platform' },
  },
};

const ERROR_SCREENS: Record<string, string> = {
  'sso-access-denied': 'access_denied',
  'sso-deactivated': 'deactivated',
  'sso-not-invited': 'not_invited',
  'sso-disabled': 'disabled',
  'sso-expired': 'expired',
};

let installed = false;

/**
 * Answers `/trpc/system.authInfo` (and only that) from the fixture, so no request ever leaves
 * the page. Everything else falls through to the real `fetch`, which keeps the harness honest:
 * an unexpected call still shows up in the network log the verifier reads.
 */
function installAuthInfoStub(info: AuthInfo): void {
  if (installed) return;
  installed = true;
  const real = globalThis.fetch.bind(globalThis);
  globalThis.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const href = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
    const url = new URL(href, window.location.origin);
    if (url.pathname.startsWith('/trpc/')) {
      const paths = url.pathname.replace(/^\/trpc\//, '').split(',');
      const body = paths.map((path) => ({
        result: { data: path === 'system.authInfo' ? info : null },
      }));
      return new Response(JSON.stringify(body), {
        status: 200,
        headers: { 'content-type': 'application/json' },
      });
    }
    return real(input, init);
  };
}

export function AuthHarness() {
  const [params] = useSearchParams();
  const screen = params.get('screen') ?? 'login';

  const reason = ERROR_SCREENS[screen];
  if (reason) return <SsoErrorView copy={SSO_ERRORS[reason] ?? GENERIC_SSO_ERROR} />;
  if (screen === 'sso-generic') return <SsoErrorView copy={GENERIC_SSO_ERROR} />;

  installAuthInfoStub(INFOS[screen] ?? INFOS['login']!);
  return <Login />;
}
